import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Typography,
  Button,
  Stack,
} from "@mui/material";
import { AttachMoney, Cancel, LiveTv } from "@mui/icons-material";
import Hls from "hls.js";
import { getUIStage } from "@/utils";
import { useBroadcast } from "@/services/hooks";

const LIVESTREAM_BASE = `https://storage.googleapis.com/nb-live-${getUIStage()}`;

export default function Viewer() {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const [searchParams] = useSearchParams();
  const contentKey = searchParams.get("key");

  const loadHlsStream = useCallback(() => {
    if (!contentKey) return;

    const hlsUrl = `${LIVESTREAM_BASE}/${contentKey}/manifest.m3u8`;
    console.log("Loading HLS stream:", hlsUrl);
    if (!videoRef.current) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (!Hls.isSupported()) {
      setError("Your browser does not support HLS playback.");
      return;
    }

    try {
      const hls = new Hls({
        liveDurationInfinity: true,
        backBufferLength: 30,
      });

      hls.loadSource(hlsUrl);
      hls.attachMedia(videoRef.current);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (videoRef.current) {
          videoRef.current.duration = Infinity;
          videoRef.current.play().catch((error) => {
            console.error("Playback error:", error);
            setError("Failed to start playback. Please try again.");
          });
        }
        setIsLoading(false);
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error("HLS error:", data);
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              setError("Network error. Retrying...");
              setTimeout(() => loadHlsStream(), 5000);
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              setError("Media error. Attempting to recover...");
              hls.recoverMediaError();
              break;
            default:
              setError("Fatal streaming error. Please try again later.");
              hls.destroy();
          }
        }
      });

      hlsRef.current = hls;
    } catch (err) {
      console.error("Stream initialization error:", err);
      setError("Failed to initialize stream. Please try again.");
      setIsLoading(false);
    }
  }, [contentKey]);

  useEffect(() => {
    loadHlsStream();

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [loadHlsStream]);

  return (
    <>
      <PageAppBar />
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            border: "1px solid #343332",
            aspectRatio: "1 / 1",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          }}
        >
          <video
            ref={videoRef}
            controls
            playsInline
            autoPlay
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>
    </>
  );
}

function PageAppBar() {
  const navigate = useNavigate();
  const { artistKey } = useParams();

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: "rgba(19,19,19,.9)",
      }}
    >
      <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box
          className="artist-logo"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <LiveTv color="secondary" />
          <Typography
            sx={{
              fontFamily: "'Montserrat Alternates', sans-serif",
              fontWeight: 600,
              fontSize: "1.3rem",
              mb: 0,
              ml: 1.5,
            }}
          >
            {artistKey} live
          </Typography>
        </Box>
        <IconButton onClick={() => navigate(-1)} aria-label="close stream">
          <Cancel color="info" />
        </IconButton>
      </Toolbar>
    </AppBar>
  );
}
